import React, { useState } from 'react';
import Navbar from '@/components/zera/Navbar';
import Footer from '@/components/zera/Footer';
import { Calendar, Users, MapPin, ArrowRight, ArrowLeft, CheckCircle2, Send } from 'lucide-react';

const steps = ['Experience', 'Details', 'Preferences', 'Confirm'];

const experienceOptions = [
  { id: 'riviera', name: 'Albanian Riviera', desc: 'Beaches, coves & coastal luxury' },
  { id: 'heritage', name: 'Heritage Cities', desc: 'UNESCO sites & Ottoman culture' },
  { id: 'alps', name: 'Albanian Alps', desc: 'Mountain trekking & alpine retreats' },
  { id: 'yacht', name: 'Yacht Charter', desc: 'Private sailing along the coast' },
  { id: 'villa', name: 'Villa Retreat', desc: 'Exclusive private villa stays' },
  { id: 'culinary', name: 'Culinary Journey', desc: 'Food, wine & local flavors' },
  { id: 'corporate', name: 'Corporate Event', desc: 'Business retreats & team building' },
  { id: 'custom', name: 'Custom Journey', desc: 'Fully bespoke itinerary' },
];

const accommodationOptions = ['Luxury Hotel', 'Boutique Hotel', 'Private Villa', 'Mountain Lodge', 'Yacht', 'Mixed'];
const budgetOptions = ['€1,000 - €3,000', '€3,000 - €5,000', '€5,000 - €10,000', '€10,000 - €20,000', '€20,000+'];

const Booking: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    experience: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    arrivalDate: '',
    departureDate: '',
    guests: '2',
    accommodation: '',
    budget: '',
    interests: [] as string[],
    specialRequests: '',
  });

  const toggleInterest = (interest: string) => {
    setFormData((prev) => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter((i) => i !== interest)
        : [...prev.interests, interest],
    }));
  };

  const canProceed = () => {
    if (currentStep === 0) return !!formData.experience;
    if (currentStep === 1) return formData.firstName && formData.lastName && formData.email && formData.arrivalDate;
    return true;
  };

  const handleSubmit = () => {
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FAF8F3]">
        <Navbar />
        {/* Dark header area for transparent navbar */}
        <div className="bg-[#0D1B2A] pt-28 pb-16">
          <div className="max-w-xl mx-auto text-center px-6">
            <div className="w-20 h-20 rounded-full bg-[#C9A961]/20 flex items-center justify-center mx-auto mb-8">
              <Send className="w-8 h-8 text-[#C9A961]" />
            </div>
            <h1 className="font-serif text-4xl text-white font-light mb-4">Faleminderit</h1>
            <p className="font-sans text-sm text-white/70 leading-relaxed mb-2">Thank you, {formData.firstName}. Your journey request has been received.</p>
            <p className="font-sans text-sm text-white/50 leading-relaxed">Our concierge team will craft your personalized Albanian itinerary and reach out within 24 hours.</p>
          </div>
        </div>
        <div className="py-12">
          <div className="max-w-xl mx-auto px-6">
            <div className="bg-white border border-[#E8DFD0] p-8 text-left mb-8">
              <h3 className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#C9A961] mb-4">Booking Summary</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="font-sans text-xs text-[#999]">Experience</span>
                  <span className="font-sans text-xs text-[#1A3B5C]">{experienceOptions.find(e => e.id === formData.experience)?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-sans text-xs text-[#999]">Dates</span>
                  <span className="font-sans text-xs text-[#1A3B5C]">{formData.arrivalDate} — {formData.departureDate || 'Flexible'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-sans text-xs text-[#999]">Guests</span>
                  <span className="font-sans text-xs text-[#1A3B5C]">{formData.guests}</span>
                </div>
                {formData.budget && (
                  <div className="flex justify-between">
                    <span className="font-sans text-xs text-[#999]">Budget</span>
                    <span className="font-sans text-xs text-[#1A3B5C]">{formData.budget}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="text-center">
              <button onClick={() => { setSubmitted(false); setCurrentStep(0); setFormData({ experience: '', firstName: '', lastName: '', email: '', phone: '', arrivalDate: '', departureDate: '', guests: '2', accommodation: '', budget: '', interests: [], specialRequests: '' }); }} className="font-sans text-[11px] tracking-[0.2em] uppercase text-[#C9A961] hover:text-[#b8944d] transition-colors">
                Plan Another Journey
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (

    <div className="min-h-screen bg-[#FAF8F3]">
      <Navbar />

      {/* Hero */}
      <section className="relative h-[50vh] w-full overflow-hidden">
        <img src="https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1775237667349_afcdbb55.jpg" alt="Plan your bespoke Albania journey — luxury travel concierge, private itineraries across the Albanian Riviera, Alps and UNESCO heritage cities" className="absolute inset-0 w-full h-full object-cover" loading="eager" fetchPriority="high" />

        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60" />
        <div className="relative z-10 h-full flex flex-col justify-end pb-16 px-6 lg:px-12 max-w-[1440px] mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-[1px] bg-[#C9A961]" />
            <span className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961]">Reservations</span>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-light text-white leading-[1.1]">
            Plan Your Journey
          </h1>
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-16 lg:py-24">
        <div className="max-w-3xl mx-auto px-6">
          {/* Progress Steps */}
          <div className="flex items-center justify-between mb-16">
            {steps.map((step, i) => (
              <div key={step} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-sans transition-all ${
                    i <= currentStep ? 'bg-[#C9A961] text-white' : 'bg-[#E8DFD0] text-[#999]'
                  }`}>
                    {i < currentStep ? <CheckCircle2 className="w-5 h-5" /> : i + 1}
                  </div>
                  <span className={`font-sans text-[9px] tracking-wider uppercase mt-2 ${
                    i <= currentStep ? 'text-[#C9A961]' : 'text-[#999]'
                  }`}>{step}</span>
                </div>
                {i < steps.length - 1 && (
                  <div className={`w-12 sm:w-20 md:w-32 h-[1px] mx-2 ${i < currentStep ? 'bg-[#C9A961]' : 'bg-[#E8DFD0]'}`} />
                )}
              </div>
            ))}
          </div>

          {/* Step 1: Experience */}
          {currentStep === 0 && (
            <div>
              <h2 className="font-serif text-2xl text-[#1A3B5C] font-light mb-2">Choose Your Experience</h2>
              <p className="font-sans text-sm text-[#999] mb-8">Select the type of Albanian journey that speaks to you.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {experienceOptions.map((exp) => (
                  <button
                    key={exp.id}
                    onClick={() => setFormData({ ...formData, experience: exp.id })}
                    className={`p-6 text-left border transition-all ${
                      formData.experience === exp.id
                        ? 'border-[#C9A961] bg-[#C9A961]/5'
                        : 'border-[#E8DFD0] hover:border-[#C9A961]/50 bg-white'
                    }`}
                  >
                    <h3 className="font-serif text-base text-[#1A3B5C] mb-1">{exp.name}</h3>
                    <p className="font-sans text-xs text-[#999]">{exp.desc}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Details */}
          {currentStep === 1 && (
            <div>
              <h2 className="font-serif text-2xl text-[#1A3B5C] font-light mb-2">Your Details</h2>
              <p className="font-sans text-sm text-[#999] mb-8">Tell us about yourself and your travel dates.</p>
              <div className="space-y-6 bg-white p-8 border border-[#E8DFD0]">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#999] block mb-2">First Name *</label>
                    <input type="text" required value={formData.firstName} onChange={(e) => setFormData({ ...formData, firstName: e.target.value })} className="w-full border-b border-[#E8DFD0] py-3 font-sans text-sm text-[#1A3B5C] focus:outline-none focus:border-[#C9A961] bg-transparent" />
                  </div>
                  <div>
                    <label className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#999] block mb-2">Last Name *</label>
                    <input type="text" required value={formData.lastName} onChange={(e) => setFormData({ ...formData, lastName: e.target.value })} className="w-full border-b border-[#E8DFD0] py-3 font-sans text-sm text-[#1A3B5C] focus:outline-none focus:border-[#C9A961] bg-transparent" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#999] block mb-2">Email *</label>
                    <input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full border-b border-[#E8DFD0] py-3 font-sans text-sm text-[#1A3B5C] focus:outline-none focus:border-[#C9A961] bg-transparent" />
                  </div>
                  <div>
                    <label className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#999] block mb-2">Phone</label>
                    <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="w-full border-b border-[#E8DFD0] py-3 font-sans text-sm text-[#1A3B5C] focus:outline-none focus:border-[#C9A961] bg-transparent" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#999] block mb-2">Arrival Date *</label>
                    <input type="date" required value={formData.arrivalDate} onChange={(e) => setFormData({ ...formData, arrivalDate: e.target.value })} className="w-full border-b border-[#E8DFD0] py-3 font-sans text-sm text-[#1A3B5C] focus:outline-none focus:border-[#C9A961] bg-transparent" />
                  </div>
                  <div>
                    <label className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#999] block mb-2">Departure Date</label>
                    <input type="date" value={formData.departureDate} onChange={(e) => setFormData({ ...formData, departureDate: e.target.value })} className="w-full border-b border-[#E8DFD0] py-3 font-sans text-sm text-[#1A3B5C] focus:outline-none focus:border-[#C9A961] bg-transparent" />
                  </div>
                  <div>
                    <label className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#999] block mb-2">Guests</label>
                    <select value={formData.guests} onChange={(e) => setFormData({ ...formData, guests: e.target.value })} className="w-full border-b border-[#E8DFD0] py-3 font-sans text-sm text-[#1A3B5C] focus:outline-none focus:border-[#C9A961] bg-transparent">
                      {[1, 2, 3, 4, 5, 6, 7, 8, 10, 12, 15, 20].map((n) => (
                        <option key={n} value={n}>{n} {n === 1 ? 'Guest' : 'Guests'}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Preferences */}
          {currentStep === 2 && (
            <div>
              <h2 className="font-serif text-2xl text-[#1A3B5C] font-light mb-2">Your Preferences</h2>
              <p className="font-sans text-sm text-[#999] mb-8">Help us personalize your experience.</p>
              <div className="space-y-8 bg-white p-8 border border-[#E8DFD0]">
                <div>
                  <label className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#999] block mb-4">Accommodation Style</label>
                  <div className="flex flex-wrap gap-3">
                    {accommodationOptions.map((opt) => (
                      <button
                        key={opt}
                        onClick={() => setFormData({ ...formData, accommodation: opt })}
                        className={`font-sans text-[10px] tracking-wider uppercase px-5 py-2.5 border transition-all ${
                          formData.accommodation === opt ? 'border-[#C9A961] bg-[#C9A961] text-white' : 'border-[#E8DFD0] text-[#999] hover:border-[#C9A961]'
                        }`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#999] block mb-4">Budget Range</label>
                  <div className="flex flex-wrap gap-3">
                    {budgetOptions.map((opt) => (
                      <button
                        key={opt}
                        onClick={() => setFormData({ ...formData, budget: opt })}
                        className={`font-sans text-[10px] tracking-wider uppercase px-5 py-2.5 border transition-all ${
                          formData.budget === opt ? 'border-[#C9A961] bg-[#C9A961] text-white' : 'border-[#E8DFD0] text-[#999] hover:border-[#C9A961]'
                        }`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#999] block mb-4">Interests (select all that apply)</label>
                  <div className="flex flex-wrap gap-3">
                    {['Beach & Coast', 'Mountains & Hiking', 'History & Culture', 'Food & Wine', 'Adventure Sports', 'Wellness & Spa', 'Photography', 'Nightlife'].map((interest) => (
                      <button
                        key={interest}
                        onClick={() => toggleInterest(interest)}
                        className={`font-sans text-[10px] tracking-wider uppercase px-5 py-2.5 border transition-all ${
                          formData.interests.includes(interest) ? 'border-[#C9A961] bg-[#C9A961] text-white' : 'border-[#E8DFD0] text-[#999] hover:border-[#C9A961]'
                        }`}
                      >
                        {interest}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#999] block mb-2">Special Requests</label>
                  <textarea rows={4} value={formData.specialRequests} onChange={(e) => setFormData({ ...formData, specialRequests: e.target.value })} placeholder="Dietary requirements, accessibility needs, celebrations..." className="w-full border-b border-[#E8DFD0] py-3 font-sans text-sm text-[#1A3B5C] focus:outline-none focus:border-[#C9A961] bg-transparent resize-none" />
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Confirm */}
          {currentStep === 3 && (
            <div>
              <h2 className="font-serif text-2xl text-[#1A3B5C] font-light mb-2">Review & Confirm</h2>
              <p className="font-sans text-sm text-[#999] mb-8">Please review your journey details before submitting.</p>
              <div className="bg-white border border-[#E8DFD0] p-8 space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#999] block mb-1">Experience</span>
                    <span className="font-sans text-sm text-[#1A3B5C]">{experienceOptions.find(e => e.id === formData.experience)?.name}</span>
                  </div>
                  <div>
                    <span className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#999] block mb-1">Guests</span>
                    <span className="font-sans text-sm text-[#1A3B5C]">{formData.guests}</span>
                  </div>
                  <div>
                    <span className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#999] block mb-1">Name</span>
                    <span className="font-sans text-sm text-[#1A3B5C]">{formData.firstName} {formData.lastName}</span>
                  </div>
                  <div>
                    <span className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#999] block mb-1">Email</span>
                    <span className="font-sans text-sm text-[#1A3B5C]">{formData.email}</span>
                  </div>
                  <div>
                    <span className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#999] block mb-1">Dates</span>
                    <span className="font-sans text-sm text-[#1A3B5C]">{formData.arrivalDate} — {formData.departureDate || 'Flexible'}</span>
                  </div>
                  {formData.accommodation && (
                    <div>
                      <span className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#999] block mb-1">Accommodation</span>
                      <span className="font-sans text-sm text-[#1A3B5C]">{formData.accommodation}</span>
                    </div>
                  )}
                  {formData.budget && (
                    <div>
                      <span className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#999] block mb-1">Budget</span>
                      <span className="font-sans text-sm text-[#1A3B5C]">{formData.budget}</span>
                    </div>
                  )}
                </div>
                {formData.interests.length > 0 && (
                  <div>
                    <span className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#999] block mb-2">Interests</span>
                    <div className="flex flex-wrap gap-2">
                      {formData.interests.map((i) => (
                        <span key={i} className="font-sans text-[9px] tracking-wider uppercase px-3 py-1 bg-[#F5F1E8] text-[#999]">{i}</span>
                      ))}
                    </div>
                  </div>
                )}
                {formData.specialRequests && (
                  <div>
                    <span className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#999] block mb-1">Special Requests</span>
                    <p className="font-sans text-sm text-[#666]">{formData.specialRequests}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-10">
            {currentStep > 0 ? (
              <button onClick={() => setCurrentStep(currentStep - 1)} className="font-sans text-[11px] tracking-[0.2em] uppercase text-[#999] flex items-center gap-2 hover:text-[#1A3B5C] transition-colors">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
            ) : <div />}
            {currentStep < steps.length - 1 ? (
              <button
                onClick={() => canProceed() && setCurrentStep(currentStep + 1)}
                disabled={!canProceed()}
                className={`font-sans text-[11px] tracking-[0.25em] uppercase px-10 py-4 flex items-center gap-2 transition-all ${
                  canProceed() ? 'bg-[#C9A961] text-white hover:bg-[#b8944d]' : 'bg-[#E8DFD0] text-[#999] cursor-not-allowed'
                }`}
              >
                Continue <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button onClick={handleSubmit} className="font-sans text-[11px] tracking-[0.25em] uppercase px-10 py-4 bg-[#1A3B5C] text-white hover:bg-[#2a5580] transition-all flex items-center gap-2">
                Submit Request <Send className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Booking;
